import React, { useState } from 'react';
import { request } from '../../Utils/request';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import CryptoJS from 'crypto-js';
import { useNavigate } from 'react-router-dom';
import OtpInput from 'react-otp-input';
// Firestore imports
import { handleUserEntryInFirestore } from '../../services/CommonApiCall';
import { login } from '../../store/slices/AuthSlice';
import { setUser } from '../../store/slices/User';
import { useDispatch } from 'react-redux';
import { generateAndRegisterFcmToken } from '../../Utils/fcmTokenHelper';
import COLORS from '../../Utils/colors';

type SignupStep = 'form' | 'otp';

const Signup = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [step, setStep] = useState<SignupStep>('form');
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    password: ''
  });
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [usernameStatus, setUsernameStatus] = useState<string>('');
  const [usernameStatusType, setUsernameStatusType] = useState<'success' | 'error' | ''>('');
  const [isUsernameValid, setIsUsernameValid] = useState<boolean>(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{
    firstName?: string;
    username?: string;
    email?: string;
  }>({});
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const hashPassword = (password: string, uniqueKey: string): string => {
    const hash = CryptoJS.HmacSHA256(password, uniqueKey);
    return hash.toString();
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let nextValue = value;
    if (name === 'firstName') {
      nextValue = value.slice(0, 100);
    }
    if (name === 'username') {
      nextValue = value.toLowerCase().replace(/\s/g, '').slice(0, 100);
    }

    setForm({ ...form, [name]: nextValue });
    if (name === 'firstName') {
      setFieldErrors((prev) => ({ ...prev, firstName: undefined }));
    }
    if (name === 'username') {
      setFieldErrors((prev) => ({ ...prev, username: undefined }));
    }
    if (name === 'email') {
      setFieldErrors((prev) => ({ ...prev, email: undefined }));
    }
  };

  const checkUsername = async (username: string) => {
    console.log(username);
    if (!username) {
      setUsernameStatus('');
      setUsernameStatusType('');
      setIsUsernameValid(false);
      return;
    }
    try {
      const res = await request<any, typeof form>(`/profile/check-username/${username}`, {
        method: 'GET',
      });
      console.log('res', res);
      if (res.status === true ) {
        setUsernameStatus(res.data.message || 'Username is available');
        setUsernameStatusType('success');
        setIsUsernameValid(true);
        console.log('username is available');
      } else {
        setUsernameStatus(res.data.message || 'Username is not available');
        setUsernameStatusType('error');
        setIsUsernameValid(false);
        console.log('username is not available', res.data.message);
      }
    } catch (error: any) {
      setUsernameStatus(error.message || 'Error checking username');
      setUsernameStatusType('error');
      setIsUsernameValid(false);
      console.log('error checking username');
    }
  }

  /**
   * After OTP verification: login, FCM, profile, Firestore, then redirect.
   */
  const completeSignupAndLogin = async (res: { token?: string; user?: any; success?: boolean }) => {
    let fcmToken: string | null = null;
    if (res.token) {
      dispatch(login(res.token));
      try {
        fcmToken = await generateAndRegisterFcmToken();
      } catch (tokenError) {
        console.warn('Failed to generate FCM token at signup:', tokenError);
      }
    }

    if (res.user?.id) {
      try {
        const profileResponse = await request<any, null>(`/profile/${res.user.id}`, {
          method: 'GET',
        });
        if (profileResponse?.data) {
          dispatch(setUser(profileResponse.data));
        } else if (res.user) {
          dispatch(setUser(res.user));
        }
      } catch (profileError) {
        console.warn('Failed to fetch user profile after signup, falling back to response payload.', profileError);
        if (res.user) {
          dispatch(setUser(res.user));
        }
      }
    }

    if (res?.success === true && res?.user?.id) {
      const userdataObj = {
        username: res.user?.username,
        email: res.user?.email,
        firstName: res.user?.firstName,
        lastName: res.user?.lastName,
        fcmToken: fcmToken ?? null,
      };
      handleUserEntryInFirestore(res.user.id, userdataObj, fcmToken || '');
    }

    setMessage('Signup successful! Redirecting...');
    navigate('/');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitAttempted(true);
    setLoading(true);
    setMessage(null);
    setError(null);

    const nextErrors: { firstName?: string; username?: string; email?: string } = {};
    if (!form.firstName.trim()) {
      nextErrors.firstName = 'Name is required';
    } else if (form.firstName.trim().length > 100) {
      nextErrors.firstName = 'Name must be 100 characters or less';
    }
    const usernameTrim = form.username.trim();
    if (!usernameTrim) {
      nextErrors.username = 'Username is required';
    } else if (usernameTrim.length > 100) {
      nextErrors.username = 'Username must be 100 characters or less';
    } else if (usernameTrim !== usernameTrim.toLowerCase()) {
      nextErrors.username = 'Username must be lowercase';
    } else if (!/^[a-z0-9._-]+$/.test(usernameTrim)) {
      nextErrors.username = 'Username can only use lowercase letters, numbers, dots, underscores, and hyphens';
    }
    if (!form.email.trim()) {
      nextErrors.email = 'Email is required';
    } else if (!emailRegex.test(form.email.trim())) {
      nextErrors.email = 'Please enter a valid email address';
    }
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      setLoading(false);
      return;
    }
    setFieldErrors({});

    if (!isUsernameValid) {
      setError('Please choose a valid username before signing up.');
      setLoading(false);
      return;
    }
    try {
      const hashedPassword = hashPassword(form.password, 'ifinds');
      const formWithHashedPassword = { ...form, password: hashedPassword };
      const res = await request<any, typeof form>('/auth/signup', {
        method: 'POST',
        body: formWithHashedPassword,
      });

      setMessage(res?.message || 'Verification code sent to your email.');
      setStep('otp');
    } catch (err: any) {
      setError(err.message || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length !== 6) {
      setError('Please enter the 6-digit code.');
      return;
    }
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      const res = await request<any, { otp: string; email: string }>('/auth/signup/verify-email', {
        method: 'POST',
        body: { otp, email: form.email },
      });
      await completeSignupAndLogin(res);
    } catch (err: any) {
      setError(err.message || 'Invalid or expired code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResendLoading(true);
    setError(null);
    setMessage(null);
    try {
      const res = await request<any, { email: string }>('/auth/signup/resend-otp', {
        method: 'POST',
        body: { email: form.email },
      });
      setMessage(res?.message || 'Verification code resent.');
    } catch (err: any) {
      setError(err.message || 'Failed to resend code.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleBackToForm = () => {
    setStep('form');
    setOtp('');
    setError(null);
    setMessage(null);
  };

  const displayEmail = form.email ? form.email.replace(/(.{2})(.*)(@.*)/, '$1***$3') : '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f5f5f5' }}>
      <Paper elevation={3} style={{ padding: 32, minWidth: 350, maxWidth: 400 }}>
        {step === 'form' ? (
          <>
            <Typography variant="h5" align="center" gutterBottom>Signup</Typography>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <TextField
                name="firstName"
                label="First Name"
                value={form.firstName}
                onChange={handleChange}
                required
                inputProps={{ maxLength: 100 }}
                error={submitAttempted && !!fieldErrors.firstName}
                helperText={submitAttempted && fieldErrors.firstName ? fieldErrors.firstName : ''}
              />
              <TextField name="lastName" label="Last Name" value={form.lastName} onChange={handleChange} required />
              <TextField
                name="username"
                label="Username"
                value={form.username}
                onChange={handleChange}
                onBlur={() => checkUsername(form.username)}
                required
                inputProps={{ maxLength: 100 }}
                error={submitAttempted && !!fieldErrors.username}
                helperText={submitAttempted && fieldErrors.username ? fieldErrors.username : ''}
              />
              {usernameStatus !== '' && (
                <Alert severity={usernameStatusType === 'success' ? 'success' : 'error'} style={{ marginTop: 16 }}>
                  {usernameStatus}
                </Alert>
              )}
              <TextField
                name="email"
                label="Email"
                type="email"
                value={form.email}
                onChange={handleChange}
                required
                error={submitAttempted && !!fieldErrors.email}
                helperText={submitAttempted && fieldErrors.email ? fieldErrors.email : ''}
              />
              <TextField name="password" label="Password" type="password" value={form.password} onChange={handleChange} required />
              <Typography variant="body2" color="text.secondary" style={{ marginTop: 8 }}>{usernameStatusType}</Typography>
              <Button type="submit" variant="contained"  style={{ marginTop: 8 ,background:COLORS.primary}}>
                {loading ? 'Signing up...' : 'Signup'}
              </Button>
            </form>
          </>
        ) : (
          <>
            <Typography variant="h5" align="center" gutterBottom>Verify your email</Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
              We sent a 6-digit code to {displayEmail}
            </Typography>
            <form onSubmit={handleVerifyOtp} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 1 }}>
                <OtpInput
                  value={otp}
                  onChange={setOtp}
                  numInputs={6}
                  renderInput={(props) => (
                    <input
                      {...props}
                      style={{
                        width: '42px',
                        height: '48px',
                        fontSize: '1.25rem',
                        textAlign: 'center',
                        border: '1px solid ',
                        borderColor:COLORS.primary,
                        borderRadius: '8px',
                        outline: 'none',
                        margin: '0 4px',
                        boxSizing: 'border-box',
                      }}
                    />
                  )}
                  containerStyle={{ display: 'flex', justifyContent: 'center', gap: 4 }}
                  shouldAutoFocus
                />
              </Box>
              <Button type="submit" variant="contained" color="primary" sx={{background:COLORS.primary}} fullWidth disabled={loading || otp.length !== 6}>
                {loading ? 'Verifying...' : 'Verify & continue'}
              </Button>
              <Button type="button" variant="outlined" onClick={handleBackToForm} fullWidth disabled={loading}>
                Back
              </Button>
            </form>
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ display: 'inline' }}>
                Didn&apos;t receive the code?{' '}
              </Typography>
              <Link component="button" variant="body2" onClick={handleResendOtp} disabled={resendLoading} sx={{ fontWeight: 600 }}>
                {resendLoading ? 'Sending...' : 'Resend'}
              </Link>
            </Box>
          </>
        )}
        {message && <Alert severity="success" style={{ marginTop: 16 }}>{message}</Alert>}
        {error && <Alert severity="error" style={{ marginTop: 16 }}>{error}</Alert>}
      </Paper>
    </div>
  );
};

export default Signup; 